import { useEffect, useMemo, useState } from 'react'

import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Popover,
  Portal,
  Spacer,
  Spinner,
  Square,
  Text,
} from '@chakra-ui/react'
import {
  CollapseIcon,
  DeleteIcon,
  ExpandIcon,
  FocusItemIcon,
  HideIcon,
  OrderGrabIcon,
  ResetIcon,
  ShowIcon,
  SuccessIcon,
  UnsavedIcon,
  WarningTwoIcon,
} from '@editor/assets/icons'
import AutoComplete from '@editor/components/AutoComplete'
import { Tooltip } from '@editor/components/ui/tooltip'
import { CursorState } from '@editor/config/constants/cursor'
import { INFO_PANEL_WIDTH } from '@editor/config/styles'
import { EditorPanelItem } from '@editor/interfaces/editor'
import { DEFAULT_INSPECTION_GROUPS, isInspectionGroup } from '@editor/services/InspectionGroup'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setCursor } from '@editor/store/tool'
import Chrome, { ChromeInputType } from '@uiw/react-color-chrome'
import { useSelector } from 'react-redux'
import { useThrottledCallback } from 'use-debounce'

import { setDraggingItem } from '../../store/inspectionGroup'
import useElementsPanel from '../hooks/useElementsPanel'
import { checkIsWorking, getId, getIdsForDeletion } from '../utils'

interface LayerProps {
  item: EditorPanelItem
  depth: number
  isParentWorking?: boolean
  collapsible?: boolean
  editMode?: boolean
  onUpdated?: (item: EditorPanelItem) => void
  onColorChanged?: (color: string) => void
  onCheckToggled?: (ids: string[], selected: boolean) => void

  /**
   * Custom function to handle selection.
   * @param id The ID (as returned by getId) of the item to select.
   */
  onSelect?: (ids: string[], selected: boolean) => void

  /**
   * Custom function to handle visibility toggling.
   *
   * @param ids The IDs of the items to toggle visibility for.
   * @param invisible Whether to set the items as invisible (true) or visible (false).
   */
  onVisibilityToggled?: (ids: string[], invisible: boolean) => void

  /**
   * If true, this is for a group layer.
   * This is used to group items together in the panel.
   */
  forGroup?: boolean

  /**
   * If true, this is a shadow layer for grabbed items.
   * This is the element that will be dragged around when an item is grabbed.
   * In this mode, everything is read-only, and the layer will not be clickable.
   */
  isShadowClone?: boolean

  /**
   * If the name field is editable, this function will be called to validate the name.
   * If it returns a string, it will be shown as an error message.
   * If it returns true, the name is valid.
   *
   * @param name The name to validate
   * @param targetId The ID of the item being validated
   * @returns A string error message if validation fails, or true if validation passes
   */
  validateName?: (name: string, targetId: string) => string | boolean
  forceLayerFullOpacity?: boolean
}

export const Layer = ({
  item: current,
  depth,
  isParentWorking = false,
  isShadowClone = false,
  forGroup = false,
  collapsible = true,
  editMode = false,
  onUpdated,
  onColorChanged,
  onCheckToggled,
  onSelect: onSelectLayer,
  onVisibilityToggled,
  validateName,
  forceLayerFullOpacity = false,
}: LayerProps) => {
  const {
    key,
    name,
    color,
    hideColorPicker = false,
    displayName,
    dropdown,
    children,
    item,
    inspectionItem,
    isGrouped = false,
    isCountable = false,
    isOrderable = false,
    isCollapsible = false,
    isWorking = false,
    isSaving = false,
    isDeleting = false,
    isSuccess = false,
    isLoading = false,
    isVirtualContainer = false,
    isMarkedForDeletion = false,
    textStyle,
    onSelect = true,
    onHover,
    onDelete,
    onReset,
    toggleVisibility,
    errorMessage,
  } = current
  const layerId = getId(current)

  // Store
  const dispatch = useAppDispatch()
  const isElementDeleting = useSelector((state: RootState) => state.editor.isElementDeleting)
  const deletingElementIds = useSelector((state: RootState) => state.toolUtil.core.deletingElementIds)
  const draggingItemKey = useSelector((state: RootState) => state.inspectionGroup.draggingItemKey)
  const editedGroupKeys = useSelector((state: RootState) => state.inspectionGroup.editedGroupKeys)
  const editedElementIds = useSelector((state: RootState) => state.toolUtil.core.editedElementIds)
  const savingElementIds = useSelector((state: RootState) => state.toolUtil.core.savingElementIds)
  const isBatchSaving = useSelector((state: RootState) => state.editor.isBatchSaving)
  const isProcessing =
    isDeleting || (isElementDeleting && deletingElementIds.includes(layerId)) || savingElementIds.includes(layerId)

  // Hook
  const {
    collapsed,
    isInvisible,
    isSelected,
    isShapeSelectable,
    isHoverable,
    isChecked,
    isFocused,
    canBeChecked,
    handleToggleVisibility,
    handleDelete,
    handleHover,
    handleCheck,
    handleClick,
    handleFocusItem,
    setCollapsed,
  } = useElementsPanel(current, onUpdated, onSelectLayer, onCheckToggled, onVisibilityToggled, forGroup)

  // States
  const [layerColor, setLayerColor] = useState<string | undefined>(color)
  const [nameInputError, setNameInputError] = useState<string | null>(null)

  // Flags if certain buttons should be shown
  const hasWorkingAnscOrDesc = isParentWorking || checkIsWorking(current)
  const hasSpacer = !dropdown && !editMode
  const hasCollapseToggle = (collapsible && children && children.length > 0) || isCollapsible
  const hasBeenGrabbed = useMemo(() => draggingItemKey === key, [draggingItemKey, key])
  const hasOrderButton = isOrderable

  // Derived states. If it's a flag, prefix with `canBeXX`. Leave `isXX` for config value for easier figuring out where it comes from.
  const canBeDeleted =
    !!((item && onDelete !== false) || onDelete) && !isElementDeleting && !dropdown && !hasBeenGrabbed && !isShadowClone
  const canBeCollapsed = !hasBeenGrabbed && children && children.length > 0 && !isShadowClone
  const canBeDragged = hasOrderButton && !isShadowClone && !hasBeenGrabbed

  // Check if layer is currently being edited or saved (disable delete during these states)
  // Note: editedElementIds uses inspection_item_id, but getId() may return shape_id, so check both
  const inspectionItemId = current.inspectionItem?.inspection_item_id
  const isDirty = useMemo(() => {
    const idsToCheck = inspectionItemId ? [layerId, inspectionItemId] : [layerId]
    return (
      isBatchSaving ||
      editedGroupKeys.includes(key) ||
      idsToCheck.some((id) => editedElementIds.includes(id) || savingElementIds.includes(id))
    )
  }, [isBatchSaving, editedGroupKeys, editedElementIds, savingElementIds, key, layerId, inspectionItemId])

  /**
   * Handle color change event
   *
   * @param color
   */
  const handleChangeColor = useThrottledCallback((clr: string) => {
    if (onColorChanged) {
      onColorChanged(clr)
    }
  }, 1000 / 30)

  /**
   * Handle grab start event.
   *
   * @param e Mouse event
   */
  const handleGrabStart = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!canBeDragged) return

    e.stopPropagation()

    dispatch(setCursor(CursorState.GRABBING))
    dispatch(setDraggingItem(key))
  }

  /**
   * Generate the class name for the layer item based on its state.
   */
  const layerClass = useMemo(() => {
    const classes = ['elist-layer-item']

    if (isWorking || hasWorkingAnscOrDesc) {
      classes.push('working')
    }

    if (isSuccess) {
      classes.push('success')
    }

    if (errorMessage) {
      classes.push('error')
    }

    if (hasBeenGrabbed) {
      classes.push('grabbed')
    }

    if (isShadowClone) {
      classes.push('shadow-clone')
    }

    if (isGrouped) {
      classes.push('grouped')
    }

    if (isInspectionGroup(item)) {
      classes.push('inspection-group')
    }

    return classes.join(' ')
  }, [isWorking, isSuccess, isShadowClone, hasBeenGrabbed, hasWorkingAnscOrDesc, isGrouped, errorMessage, item])

  const containerClass = useMemo(() => {
    const classes = ['elist-layer-container']

    if (isInspectionGroup(item)) {
      classes.push('inspection-group')
    }

    return classes.join(' ')
  }, [item])

  const childrenCount = useMemo(
    () => (children || []).filter((child) => (!child.isGrouped && !forGroup) || forGroup).length,
    [children, forGroup],
  )

  /**
   * Update layerColor when color prop changes
   */
  useEffect(() => {
    setLayerColor(color)
  }, [color])

  if ((isGrouped && !forGroup && !isShadowClone) || (isVirtualContainer && !childrenCount)) {
    return null
  }

  return (
    <Tooltip
      content={<Text color="orange">{errorMessage}</Text>}
      showArrow
      disabled={!errorMessage}
      positioning={{
        placement: 'left',
      }}
    >
      <Box width="100%" className={containerClass}>
        <HStack
          width="100%"
          gap={0}
          px={2}
          opacity={
            isShapeSelectable || isWorking || hasWorkingAnscOrDesc || errorMessage || forceLayerFullOpacity ? 1 : 0.5
          }
          borderTop="1px solid transparent"
          borderBottom="1px solid transparent"
          cursor={isHoverable ? 'pointer' : 'default'}
          backgroundColor={isSelected ? '#3F444E' : undefined}
          _hover={
            isHoverable
              ? {
                  borderTop: '1px solid {colors.gray2.600}',
                  borderBottom: '1px solid {colors.gray2.600}',
                  backgroundColor: isSelected ? 'whiteAlpha.50' : 'gray2.900',
                }
              : undefined
          }
          onClick={handleClick}
          onMouseEnter={onSelect || onHover ? () => handleHover(true) : undefined}
          onMouseLeave={onSelect || onHover ? () => handleHover(false) : undefined}
          position="relative"
          className={layerClass}
          data-id={layerId}
          data-element-key={isShadowClone ? `shadow-${key}` : key}
          w={isShadowClone ? INFO_PANEL_WIDTH : undefined}
        >
          {isSelected ? (
            <Box width="5px" backgroundColor="#3F444E" height="100%" position="absolute" top={0} left={0} />
          ) : null}

          {/* indentation for children */}
          <Box width={`${depth * 12}px`} id="depth-indent" />

          {/* Ordering grab icon */}
          {hasOrderButton ? (
            <IconButton
              minW={{
                base: 7,
                lg: 6,
              }}
              aria-label="reorder"
              variant="panel-icon"
              className="grabber"
              onMouseDown={handleGrabStart}
            >
              <OrderGrabIcon />
            </IconButton>
          ) : null}

          {!hasOrderButton && depth >= 0 ? <Square size={6} id="no-order-indent" /> : null}

          {/* Chevron for collapsing children */}
          {hasCollapseToggle ? (
            <IconButton
              minW={{
                base: 7,
                lg: 6,
              }}
              aria-label={collapsed ? 'expand' : 'collapse'}
              onClick={(e) => {
                e.stopPropagation()
                setCollapsed(!collapsed)
              }}
              variant="panel-icon"
              disabled={!canBeCollapsed}
            >
              {collapsed ? <ExpandIcon /> : <CollapseIcon />}
            </IconButton>
          ) : (
            <Square size={6} id="collapse-indent" />
          )}

          {/* Visibility toggle */}
          {item || toggleVisibility || toggleVisibility === null || (isVirtualContainer && children?.length) ? (
            <IconButton
              minW={{
                base: 7,
                lg: 6,
              }}
              aria-label={isInvisible ? 'hidden' : 'shown'}
              variant="panel-icon"
              disabled={(toggleVisibility === null && !onVisibilityToggled) || isMarkedForDeletion}
              onClick={toggleVisibility !== null || onVisibilityToggled ? handleToggleVisibility : undefined}
            >
              {isInvisible ? <Icon as={HideIcon} boxSize={4} /> : <Icon as={ShowIcon} boxSize={4} />}
            </IconButton>
          ) : (
            <Square size={6} />
          )}

          {/* Focus item toggle */}
          {inspectionItem ? (
            <IconButton
              minW={{
                lgDown: 7,
                xl: 6,
              }}
              aria-label="focus to item"
              variant="panel-icon"
              onClick={handleFocusItem}
            >
              <Icon as={FocusItemIcon} color={isFocused ? '{colors.orange.400}' : ''} boxSize={4.5} />
            </IconButton>
          ) : null}

          {/* Color */}
          {color && !hideColorPicker ? (
            <Popover.Root
              onOpenChange={(e) => {
                if (!e.open) {
                  onUpdated?.({ ...current, color: layerColor, isEditing: false })
                }
              }}
            >
              <Popover.Trigger asChild>
                <Button
                  variant="outline"
                  maxWidth={4}
                  minWidth={4}
                  width={4}
                  height={4}
                  p={0}
                  ml={1}
                  backgroundColor={layerColor}
                  borderColor="whiteAlpha.200"
                  borderRadius={4}
                  _hover={{ bg: layerColor }}
                  _focus={{ bg: layerColor }}
                  _active={{ bg: layerColor }}
                  _disabled={{ bg: layerColor }}
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                  disabled={!onColorChanged || !!errorMessage || isSaving}
                />
              </Popover.Trigger>
              <Portal>
                <Popover.Positioner>
                  <Popover.Content w="auto" onClick={(e) => e.stopPropagation()}>
                    <Chrome
                      inputType={ChromeInputType.HEXA}
                      showAlpha={false}
                      showEyeDropper={false}
                      color={layerColor}
                      onChange={(clr) => {
                        setLayerColor(clr.hex)
                        handleChangeColor(clr.hex)
                      }}
                    />
                  </Popover.Content>
                </Popover.Positioner>
              </Portal>
            </Popover.Root>
          ) : null}

          {/* Label & Icons */}
          <HStack gap={0.5} flex={hasSpacer ? undefined : 1}>
            {/* On existing items, edit mode uses only an input field */}
            {editMode && !isWorking ? (
              <Box pl={1}>
                <Tooltip
                  content={<Text color="orange">{nameInputError}</Text>}
                  showArrow
                  open={!!nameInputError}
                  positioning={{
                    placement: 'top',
                  }}
                >
                  <Input
                    defaultValue={name}
                    size="xs"
                    placeholder="グループ名"
                    onBlur={(e) => {
                      const newValue = e.currentTarget.value

                      if (validateName) {
                        const result = validateName(newValue, layerId)
                        if (typeof result === 'string') {
                          setNameInputError(result)
                          return
                        }
                      }

                      if (onUpdated) {
                        onUpdated({
                          ...current,
                          ...(newValue !== name && { name: newValue }),
                          isEditing: false,
                        })
                      }
                    }}
                    onKeyDown={(e) => {
                      setNameInputError(null)

                      if (e.key === 'Enter') {
                        e.stopPropagation()

                        const newValue = e.currentTarget.value

                        if (validateName) {
                          const result = validateName(newValue, layerId)
                          if (typeof result === 'string') {
                            setNameInputError(result)
                            return
                          }
                        }

                        if (onUpdated) {
                          onUpdated({
                            ...current,
                            ...(newValue !== name && { name: newValue }),
                            isEditing: false,
                          })
                        }
                      } else if (e.key === 'Escape') {
                        e.stopPropagation()
                        if (onUpdated) {
                          onUpdated({
                            ...current,
                            isEditing: false,
                          })
                        }
                      }
                    }}
                    autoFocus
                  />
                </Tooltip>
              </Box>
            ) : null}

            {/* Use auto-complete to suggest names when creating new group */}
            {editMode && isWorking ? (
              <Box pl={1}>
                <AutoComplete
                  defaultValue={name}
                  validate={validateName ? (str) => validateName(str, layerId) : undefined}
                  items={DEFAULT_INSPECTION_GROUPS.filter((val) => {
                    if (!validateName) return true

                    const result = validateName(val, layerId)
                    return typeof result === 'boolean' && result === true
                  }).map((val) => ({
                    id: crypto.randomUUID(),
                    name: val,
                  }))}
                  onChange={(it) => {
                    // If this is a new item, and no name is provided, delete it instead
                    if (!it.name || it.name.trim() === '') {
                      return
                    }

                    if (onUpdated) {
                      onUpdated({
                        ...current,
                        name: it.name,
                        isEditing: false,
                      })
                    }
                  }}
                  onCancel={() => handleDelete(getIdsForDeletion(current), [current])}
                  autoFocus
                />
              </Box>
            ) : null}

            {!editMode ? (
              <HStack gap={0}>
                <Text
                  key={key}
                  mx={1}
                  style={textStyle}
                  textDecoration={isMarkedForDeletion ? 'line-through' : undefined}
                >
                  {displayName || name} {isCountable && childrenCount ? `(${childrenCount})` : ''}
                </Text>

                {/* Success indicator */}
                {isSuccess ? (
                  <Flex w="28px" justifyContent="center">
                    <SuccessIcon />
                  </Flex>
                ) : null}
              </HStack>
            ) : null}

            {isWorking && !isSaving && !dropdown && !onUpdated ? <UnsavedIcon size="1.2em" /> : ''}
            {errorMessage ? <WarningTwoIcon color="red.400" /> : ''}
          </HStack>

          {
            // ## Dropdown options
            dropdown
          }

          {hasSpacer ? <Spacer /> : null}

          {/* Spinner during processing */}
          {isProcessing || isLoading || isSaving ? (
            <Flex w="28px" justifyContent="center">
              <Spinner size="xs" />
            </Flex>
          ) : null}

          {
            // ## Reset button
            onReset ? (
              <IconButton
                aria-label="reset"
                variant="panel-icon"
                color={isWorking || hasWorkingAnscOrDesc ? 'yellow.400' : undefined}
                onClick={(e) => {
                  e.stopPropagation()
                  return onReset()
                }}
              >
                <ResetIcon />
              </IconButton>
            ) : null
          }

          {
            // ## Delete button
            canBeDeleted && !isDeleting ? (
              <IconButton
                aria-label="delete"
                variant="panel-icon"
                color={isWorking ? 'yellow.400' : undefined}
                disabled={(hasWorkingAnscOrDesc && !isWorking) || isSaving || isDirty}
                onClick={(e) => {
                  e.stopPropagation()
                  return handleDelete(getIdsForDeletion(current), [current])
                }}
              >
                <DeleteIcon />
              </IconButton>
            ) : null
          }

          {/* Checkable */}
          {canBeChecked && current ? (
            <Checkbox.Root id={`checkbox-${layerId}`} variant="panel" onCheckedChange={handleCheck} checked={isChecked}>
              <Checkbox.HiddenInput />
              <Checkbox.Control>
                <Checkbox.Indicator />
              </Checkbox.Control>
            </Checkbox.Root>
          ) : null}
        </HStack>
        {!collapsed &&
          children?.map((child) => (
            <Layer
              key={child.key}
              isParentWorking={isWorking || isParentWorking}
              item={child}
              depth={depth + 1}
              forGroup={forGroup}
              forceLayerFullOpacity={forceLayerFullOpacity}
              editMode={child.isEditing}
              onSelect={onSelectLayer}
              onCheckToggled={onCheckToggled}
              onVisibilityToggled={onVisibilityToggled}
              onUpdated={onUpdated}
              onColorChanged={onColorChanged}
              validateName={validateName}
            />
          ))}
      </Box>
    </Tooltip>
  )
}
